import React, { useState } from 'react';
import { captureError } from '@/utils/errorHandler';

export default function AdminTransferAppModal({ isOpen, onClose, onSuccess, appId, appName, authFetch, showToast }) {
  const [targetUserEmail, setTargetUserEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!targetUserEmail.trim()) {
      showToast('Target user email is required', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const response = await authFetch('/api/admin/transfer-app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appId,
          targetUserEmail: targetUserEmail.trim(),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to transfer app');
      }

      const data = await response.json();
      showToast(`✅ App transferred to ${data.newOwner.email}`, 'success');
      onSuccess && onSuccess(data);
      onClose();
      setTargetUserEmail('');
    } catch (error) {
      captureError(error, {
        action: 'handleSubmit',
        component: 'AdminTransferAppModal',
        extra: { appId, targetUserEmail },
      });
      console.error('Error transferring app:', error);
      showToast(error.message || 'Failed to transfer app', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Transfer App</h2>
        <p className="text-sm text-gray-600 mb-4">
          Transfer <strong>{appName}</strong> to another user.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target User Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={targetUserEmail}
              onChange={(e) => setTargetUserEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              placeholder="user@example.com"
              required
            />
            <p className="mt-1 text-xs text-gray-500">User will be created if doesn't exist</p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-xs text-yellow-800">
              <strong>Warning:</strong> This will transfer ownership of the app. The current owner will lose access.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 px-4 py-2 bg-zapt-purple text-white rounded-lg hover:bg-zapt-purple/90 disabled:opacity-50 font-medium"
            >
              {submitting ? 'Transferring...' : 'Transfer App'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

